import { useState, useRef, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Loader2, Send, MessageSquare, Paperclip, ChevronDown, ChevronUp,
  Trash2, FileText, Image, X, AtSign, Pencil, Check,
} from "lucide-react";
import { LikeButton } from "@/components/custom/LikeButton";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import {
  api, emailApi, ProjectMessage, PinnedEmail, User,
  getMessageAttachments, uploadMessageAttachment, deleteMessageAttachment,
  fetchWithAuth,
  type ChatAttachment,
} from "@/lib/api";
import { useAuth } from "@/lib/authContext";
import { useToast } from "@/hooks/use-toast";

interface ProjectMessagesSheetProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  projectTitle: string;
}

function formatBytes(bytes: number | null) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(ct: string | null) { return ct?.startsWith("image/") ?? false; }

/** Render message body with @mention highlighting */
export function renderBody(body: string) {
  const parts = body.split(/(@\w+)/g);
  return parts.map((part, i) =>
    /^@\w+$/.test(part)
      ? <span key={i} className="text-primary font-medium bg-primary/10 rounded px-0.5">{part}</span>
      : <span key={i}>{part}</span>
  );
}

export function ProjectMessagesSheet({ open, onClose, projectId, projectTitle }: ProjectMessagesSheetProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [messageBody, setMessageBody] = useState("");
  const [pinnedOpen, setPinnedOpen] = useState(true);
  const [fileQueue, setFileQueue] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [expandedAttachments, setExpandedAttachments] = useState<Record<string, boolean>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // @mention state
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);

  const { data: staffUsers = [] } = useQuery<User[]>({
    queryKey: ["users-for-mentions"],
    queryFn: () => api.getUsers(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const mentionMatches = mentionQuery !== null
    ? staffUsers.filter(u =>
        u.name?.toLowerCase().startsWith(mentionQuery.toLowerCase()) ||
        (u.name?.toLowerCase().split(" ") ?? []).some(w => w.startsWith(mentionQuery.toLowerCase()))
      ).slice(0, 6)
    : [];

  // Reset mention index when matches change
  useEffect(() => { setMentionIndex(0); }, [mentionQuery]);

  const insertMention = useCallback((user: User) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const cursor = textarea.selectionStart ?? messageBody.length;
    const before = messageBody.slice(0, cursor);
    const after = messageBody.slice(cursor);
    const handle = (user.name ?? "Unknown").replace(/\s+/g, "");
    const replaced = before.replace(/@\w*$/, `@${handle} `);
    const next = replaced + after;
    setMessageBody(next);
    setMentionQuery(null);
    // Restore focus + move cursor after the inserted mention
    requestAnimationFrame(() => {
      textarea.focus();
      const pos = replaced.length;
      textarea.setSelectionRange(pos, pos);
    });
  }, [messageBody]);

  const handleBodyChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setMessageBody(val);
    const cursor = e.target.selectionStart ?? val.length;
    const textToCursor = val.slice(0, cursor);
    const match = textToCursor.match(/@(\w*)$/);
    if (match) {
      setMentionQuery(match[1]);
    } else {
      setMentionQuery(null);
    }
  }, []);

  const handleBodyKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery !== null && mentionMatches.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex(i => Math.min(i + 1, mentionMatches.length - 1)); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex(i => Math.max(i - 1, 0)); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(mentionMatches[mentionIndex]); return; }
      if (e.key === "Escape") { setMentionQuery(null); return; }
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSend) {
      e.preventDefault();
      postMutation.mutate();
    }
  }, [mentionQuery, mentionMatches, mentionIndex, insertMention]);

  const { data: pinnedEmails = [], isLoading: pinnedLoading } = useQuery<PinnedEmail[]>({
    queryKey: ["project-pinned-emails", projectId],
    queryFn: () => emailApi.getProjectPinnedEmails(projectId),
    enabled: open && !!projectId,
  });

  const unpinMutation = useMutation({
    mutationFn: (pinnedId: string) => emailApi.unpinEmail(pinnedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-pinned-emails", projectId] });
      toast({ title: "Email unpinned" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const { data: messages = [], isLoading } = useQuery<ProjectMessage[]>({
    queryKey: ["project-messages", projectId],
    queryFn: () => api.getProjectMessages(projectId),
    enabled: open && !!projectId,
    refetchInterval: open ? 30_000 : false,
  });

  const fetchAttachments = useCallback(async (messageId: string) => {
    return getMessageAttachments(messageId);
  }, []);

  const likeMutation = useMutation({
    mutationFn: (messageId: string) =>
      fetchWithAuth<{ liked: boolean; count: number; likers: string[] }>(`/api/project-messages/${messageId}/like`, { method: "POST" }),
    onSuccess: (res, messageId) => {
      queryClient.setQueryData<ProjectMessage[]>(["project-messages", projectId], (prev) =>
        prev?.map((msg) => (msg.id === messageId ? { ...msg, likedByMe: res.liked, likeCount: res.count, likers: res.likers } : msg))
      );
    },
    onError: (err: any) => toast({ title: "Failed to like message", description: err.message, variant: "destructive" }),
  });

  const editMutation = useMutation({
    mutationFn: ({ messageId, body }: { messageId: string; body: string }) =>
      api.updateProjectMessage(messageId, body),
    // patch in place
    onSuccess: (updated) => {
      queryClient.setQueryData<ProjectMessage[]>(["project-messages", projectId], (prev) =>
        prev?.map((msg) => (msg.id === updated.id ? { ...msg, body: updated.body, edited_at: updated.edited_at } : msg))
      );
    },
    onError: (err: any) => toast({ title: "Failed to edit message", description: err.message, variant: "destructive" }),
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: (attachmentId: string) => deleteMessageAttachment(attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["msg-attachments"] });
      toast({ title: "Attachment deleted" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const postMutation = useMutation({
    mutationFn: () => api.postProjectMessage(projectId, messageBody.trim(), false),
    onSuccess: async (newMsg: ProjectMessage) => {
      if (fileQueue.length > 0) {
        setUploading(true);
        for (const file of fileQueue) {
          try { await uploadMessageAttachment(newMsg.id, file); } catch {}
        }
        setUploading(false);
        setFileQueue([]);
      }
      setMessageBody("");
      setMentionQuery(null);
      queryClient.invalidateQueries({ queryKey: ["project-messages", projectId] });
      toast({ title: "Message sent" });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData.items);
    const imageItems = items.filter((i) => i.type.startsWith("image/"));
    if (imageItems.length === 0) return;
    e.preventDefault();
    const files = imageItems.map((i) => i.getAsFile()).filter(Boolean) as File[];
    setFileQueue((q) => [...q, ...files]);
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) setFileQueue((q) => [...q, ...files]);
    e.target.value = "";
  }, []);

  const canSend = (messageBody.trim().length > 0 || fileQueue.length > 0) && !postMutation.isPending && !uploading;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0">
        <SheetHeader className="px-5 py-4 border-b shrink-0">
          <SheetTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="w-4 h-4 text-muted-foreground" />
            {projectTitle}
            <span className="text-xs font-normal text-muted-foreground ml-1">— Team Chat</span>
          </SheetTitle>
        </SheetHeader>

        {/* Message thread */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <MessageSquare className="w-9 h-9 text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">No messages yet</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Internal team discussion for this project</p>
            </div>
          ) : (
            messages.map((m) => (
              <MessageRow
                key={m.id}
                message={m}
                fetchAttachments={fetchAttachments}
                onDeleteAttachment={(id) => deleteAttachmentMutation.mutate(id)}
                expanded={!!expandedAttachments[m.id]}
                onToggleExpand={() => setExpandedAttachments((prev) => ({ ...prev, [m.id]: !prev[m.id] }))}
                onLike={() => likeMutation.mutate(m.id)}
                onEdit={(messageId, body) => editMutation.mutateAsync({ messageId, body })}
                savingEdit={editMutation.isPending}
              />
            ))
          )}
        </div>

        {/* Pinned Emails */}
        {(pinnedEmails.length > 0 || pinnedLoading) && (
          <div className="border-t shrink-0">
            <button
              className="w-full flex items-center justify-between px-5 py-2.5 text-xs font-semibold text-muted-foreground hover:bg-muted/30 transition-colors"
              onClick={() => setPinnedOpen(v => !v)}
            >
              <span className="flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" />
                Pinned Emails ({pinnedEmails.length})
              </span>
              {pinnedOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {pinnedOpen && (
              <div className="px-5 pb-3 space-y-2">
                {pinnedLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                ) : pinnedEmails.map((pe) => (
                  <div key={pe.id} className="flex items-start justify-between gap-2 text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{pe.subject || "(no subject)"}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {pe.sender_name || pe.sender_email || "Unknown"} ·{" "}
                        {pe.received_at ? (() => { try { return format(new Date(pe.received_at), "MMM d, yyyy"); } catch { return ""; } })() : ""}
                      </p>
                      {pe.snippet && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{pe.snippet}</p>
                      )}
                    </div>
                    <button
                      className="text-muted-foreground hover:text-destructive transition-colors shrink-0 mt-0.5"
                      title="Unpin"
                      onClick={() => unpinMutation.mutate(pe.id)}
                      disabled={unpinMutation.isPending}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Compose */}
        <div className="px-5 py-4 border-t space-y-2.5 shrink-0">
          {/* @mention autocomplete */}
          {mentionQuery !== null && mentionMatches.length > 0 && (
            <div className="border rounded-md bg-popover shadow-md overflow-hidden">
              <p className="text-[10px] text-muted-foreground px-2 pt-1.5 pb-0.5 font-medium uppercase tracking-wide">Mention</p>
              {mentionMatches.map((u, i) => (
                <button
                  key={u.id}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-muted/50 transition-colors ${i === mentionIndex ? "bg-muted" : ""}`}
                  onMouseDown={e => { e.preventDefault(); insertMention(u); }}
                  onMouseEnter={() => setMentionIndex(i)}
                >
                  <span className="w-6 h-6 rounded-full bg-primary/20 text-primary text-[10px] font-bold flex items-center justify-center shrink-0">
                    {(u.name?.[0] ?? "?").toUpperCase()}
                  </span>
                  <span>{u.name}</span>
                </button>
              ))}
            </div>
          )}

          <div className="relative">
            <textarea
              ref={textareaRef}
              rows={3}
              placeholder="Internal team message… Type @ to mention someone, ⌘+Enter to send"
              value={messageBody}
              onChange={handleBodyChange}
              onKeyDown={handleBodyKeyDown}
              onPaste={handlePaste}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const files = Array.from(e.dataTransfer.files);
                if (files.length) setFileQueue((q) => [...q, ...files]);
              }}
              className="w-full resize-none text-sm rounded-md border border-input bg-background px-3 py-2 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>

          {/* File queue */}
          {fileQueue.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {fileQueue.map((f, i) => (
                <div key={i} className="flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs max-w-[200px]">
                  {f.type.startsWith("image/") ? (
                    <Image className="w-3 h-3 shrink-0 text-blue-500" />
                  ) : (
                    <FileText className="w-3 h-3 shrink-0 text-muted-foreground" />
                  )}
                  <span className="truncate">{f.name}</span>
                  <button onClick={() => setFileQueue((q) => q.filter((_, j) => j !== i))}>
                    <X className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground transition-colors"
                title="Attach file"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="w-4 h-4" />
              </button>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground transition-colors"
                title="Mention someone (@)"
                onClick={() => {
                  const ta = textareaRef.current;
                  if (!ta) return;
                  const cursor = ta.selectionStart ?? messageBody.length;
                  const next = messageBody.slice(0, cursor) + "@" + messageBody.slice(cursor);
                  setMessageBody(next);
                  setMentionQuery("");
                  requestAnimationFrame(() => {
                    ta.focus();
                    ta.setSelectionRange(cursor + 1, cursor + 1);
                  });
                }}
              >
                <AtSign className="w-4 h-4" />
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx"
              className="hidden"
              onChange={handleFileChange}
            />
            <Button
              size="sm"
              className="h-8 px-3 text-xs"
              disabled={!canSend}
              onClick={() => postMutation.mutate()}
            >
              {(postMutation.isPending || uploading) ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <><Send className="w-3.5 h-3.5 mr-1" />Send</>
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---- MessageRow with lazy-loaded attachments ----
export interface MessageRowProps {
  message: ProjectMessage;
  fetchAttachments: (id: string) => Promise<ChatAttachment[]>;
  onDeleteAttachment: (id: string) => void;
  expanded: boolean;
  onToggleExpand: () => void;
  onLike: () => void;
  // parent owns mutation so failed save keeps editor open
  onEdit?: (messageId: string, body: string) => Promise<unknown>;
  savingEdit?: boolean;
}

export function MessageRow({ message: m, fetchAttachments, onDeleteAttachment, expanded, onToggleExpand, onLike, onEdit, savingEdit }: MessageRowProps) {
  const [attachments, setAttachments] = useState<ChatAttachment[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const { user } = useAuth();

  // author only
  const canEdit = !!onEdit && !!user?.id && m.author_user_id === user.id;

  // validate edit
  const editTrimmed = editText.trim();
  const canSaveEdit = (editTrimmed.length > 0 || (attachments?.length ?? 0) > 0) && editTrimmed !== m.body;

  // close only once
  const handleSaveEdit = async () => {
    if (!onEdit || !canSaveEdit) return;
    try {
      await onEdit(m.id, editTrimmed);
      setEditing(false);
    } catch {}
  };

  const handleExpand = async () => {
    onToggleExpand();
    if (!expanded && attachments === null) {
      setLoading(true);
      try {
        const data = await fetchAttachments(m.id);
        setAttachments(data);
      } catch {}
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-foreground">
          {m.users?.name ?? m.author_name ?? "Staff"}
        </span>
        <span className="text-xs text-muted-foreground">
          {format(new Date(m.created_at), "MMM d, h:mm a")}
        </span>
        {m.edited_at && (
          <span
            className="text-xs text-muted-foreground italic"
            title={`Edited ${format(new Date(m.edited_at), "MMM d, h:mm a")}`}
          >
            (edited)
          </span>
        )}
        <LikeButton
          likedByMe={m.likedByMe}
          likeCount={m.likeCount}
          likers={m.likers}
          onClick={onLike}
          className="ml-auto"
        />
        {canEdit && !editing && (
          <button
            className="text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => { setEditing(true); setEditText(m.body); }}
            title="Edit your message"
          >
            <Pencil className="w-3 h-3" />
          </button>
        )}
        <button
          className="text-muted-foreground hover:text-foreground transition-colors"
          onClick={handleExpand}
          title="Attachments"
        >
          <Paperclip className="w-3 h-3" />
        </button>
      </div>
      {editing ? (
        <div className="space-y-1.5 mt-1">
          <Textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={2}
            className="text-sm resize-none"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canSaveEdit) {
                e.preventDefault();
                void handleSaveEdit();
              }
            }}
          />
          <div className="flex gap-1.5">
            <Button
              size="sm"
              className="h-6 text-xs px-2"
              onClick={handleSaveEdit}
              disabled={!canSaveEdit || savingEdit}
            >
              {savingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            </Button>
            <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => setEditing(false)}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
          {renderBody(m.body)}
        </p>
      )}

      {expanded && (
        <div className="mt-1.5 space-y-1">
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
          ) : !attachments || attachments.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No attachments</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {attachments.map((a) => (
                <div key={a.id} className="group relative flex items-center gap-1.5 px-2 py-1.5 bg-muted rounded text-xs">
                  {isImage(a.content_type) && a.signed_url ? (
                    <button onClick={() => setLightboxUrl(a.signed_url!)}>
                      <img src={a.signed_url} alt={a.file_name} className="h-12 w-12 object-cover rounded hover:opacity-80 transition-opacity" />
                    </button>
                  ) : (
                    <a href={a.signed_url ?? "#"} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:underline">
                      <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate max-w-[120px]">{a.file_name}</span>
                    </a>
                  )}
                  <button
                    className="absolute -top-1.5 -right-1.5 flex bg-background border rounded-full w-4 h-4 items-center justify-center text-muted-foreground/40 hover:text-destructive"
                    onClick={() => onDeleteAttachment(a.id)}
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setLightboxUrl(null)}
          onKeyDown={(e) => e.key === "Escape" && setLightboxUrl(null)}
          tabIndex={-1}
        >
          <img
            src={lightboxUrl}
            alt="Attachment"
            className="max-w-[90vw] max-h-[90vh] rounded shadow-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
